import React from "react";

import SharedEmptyState from "../EmptyState";

/**
 * EmptyState - Displayed when no statistics exist
 * Matches Figma design exactly with statistics cards illustration and organic blob background
 */
const EmptyState = () => {
  return (
    <SharedEmptyState
      illustration={<StatisticsIllustration />}
      heading="Bring your dashboard to life"
      description="All you need is a little information: after two matches have been played, detailed infographics will be displayed immediately."
      className="pt-6"
    />
  );
};

/**
 * Blob illustration with statistics card placeholders (from Figma)
 */
const StatisticsIllustration = () => {
  const scale = 0.65;

  const TeamRow = () => (
    <div className="flex items-center" style={{ gap: `${5.004 * scale}px` }}>
      <div
        className="shrink-0 rounded-full"
        style={{
          width: `${20.017 * scale}px`,
          height: `${20.017 * scale}px`,
          background: "#E5E5D6",
        }}
      />
      <div className="flex flex-col flex-1" style={{ gap: `${5.004 * scale}px` }}>
        <div
          style={{
            width: `${40.034 * scale}px`,
            height: `${7.506 * scale}px`,
            background: "#4C565D",
            borderRadius: "999px",
          }}
        />
        <div
          style={{
            width: "100%",
            height: `${7.506 * scale}px`,
            background: "#E5E5D6",
            borderRadius: "999px",
          }}
        />
      </div>
    </div>
  );

  const ChartLegendItem = () => (
    <div className="flex items-center" style={{ gap: `${3.336 * scale}px` }}>
      <div
        className="rounded-full shrink-0"
        style={{
          width: `${6.672 * scale}px`,
          height: `${6.672 * scale}px`,
          background: "#E5E5D6",
        }}
      />
      <div
        style={{
          width: `${20.017 * scale}px`,
          height: `${3.336 * scale}px`,
          background: "#4C565D",
          borderRadius: "999px",
        }}
      />
    </div>
  );

  return (
    <div
      className="relative"
      style={{
        width: `${460.23 * scale}px`,
        height: `${505.431 * scale}px`,
      }}
    >
      {/* Background blob shape */}
      <svg
        className="absolute inset-0"
        width={460.23 * scale}
        height={505.431 * scale}
        viewBox="0 0 461 506"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid meet"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M175.411 0.112232C222.717 -2.66034 255.202 46.5728 291.158 77.3414C322.39 104.068 346.306 135.09 371.269 167.714C403.767 210.185 455.066 244.408 459.713 297.622C464.526 352.722 435.151 408.264 395.673 447.127C357.364 484.84 302.465 505.037 248.625 505.427C200.452 505.776 152.598 483.331 118.78 449.132C90.5995 420.634 89.0639 376.717 82.3378 337.268C78.4417 314.416 98.7127 290.025 88.1332 269.384C68.3806 230.846 5.08241 220.153 0.266365 177.143C-3.89724 139.96 41.738 117.054 68.0929 90.4151C101.644 56.5026 127.712 2.90786 175.411 0.112232Z"
          fill="#081725"
        />
      </svg>

      {/* Statistics cards container */}
      <div
        className="absolute"
        style={{
          left: `${63.39 * scale}px`,
          top: `${73.4 * scale}px`,
        }}
      >
        {/* Top left small card */}
        <div
          className="absolute overflow-hidden"
          style={{
            left: `${(63.39 - 63.39) * scale}px`,
            top: `${(73.4 - 73.4) * scale}px`,
            width: `${161.805 * scale}px`,
            height: `${66.724 * scale}px`,
            background: "#989D99",
            borderRadius: `${13.345 * scale}px`,
          }}
        >
          <img
            src="/icons/Frame 26775.svg"
            alt=""
            className="absolute"
            style={{
              left: `${9.38 * scale}px`,
              top: `${27.5 * scale}px`,
              width: `${84.273 * scale}px`,
              height: `${31.694 * scale}px`,
            }}
          />
        </div>

        {/* Second small card */}
        <div
          className="absolute overflow-hidden"
          style={{
            left: `${(63.39 - 63.39) * scale}px`,
            top: `${(146.79 - 73.4) * scale}px`,
            width: `${161.805 * scale}px`,
            height: `${66.724 * scale}px`,
            background: "#989D99",
            borderRadius: `${13.345 * scale}px`,
          }}
        >
          <img
            src="/icons/Frame 26775.svg"
            alt=""
            className="absolute"
            style={{
              left: `${9.38 * scale}px`,
              top: `${27.5 * scale}px`,
              width: `${84.273 * scale}px`,
              height: `${31.694 * scale}px`,
            }}
          />
        </div>

        {/* Large list card */}
        <div
          className="absolute flex flex-col overflow-hidden"
          style={{
            left: `${(63.39 - 63.39) * scale}px`,
            top: `${(220.19 - 73.4) * scale}px`,
            width: `${333.618 * scale}px`,
            height: `${213.515 * scale}px`,
            background: "#989D99",
            borderRadius: `${13.345 * scale}px`,
            padding: `${13.345 * scale}px`,
            gap: `${13.345 * scale}px`,
          }}
        >
          <TeamRow />
          <TeamRow />
          <TeamRow />
          <TeamRow />
          <TeamRow />
          <TeamRow />
        </div>

        {/* Chart card */}
        <div
          className="absolute overflow-hidden"
          style={{
            left: `${(230.2 - 63.39) * scale}px`,
            top: `${(73.4 - 73.4) * scale}px`,
            width: `${166.809 * scale}px`,
            height: `${140.12 * scale}px`,
            background: "#989D99",
            borderRadius: `${13.345 * scale}px`,
          }}
        >
          <img
            src="/icons/Group 26803.svg"
            alt=""
            className="absolute"
            style={{
              left: `${11.68 * scale}px`,
              top: "50%",
              transform: "translateY(-50%)",
              width: `${103.422 * scale}px`,
              height: `${103.422 * scale}px`,
            }}
          />
          <div
            className="absolute flex flex-col"
            style={{
              left: `${121.77 * scale}px`,
              top: `${25.02 * scale}px`,
              width: `${31.694 * scale}px`,
              gap: `${3.336 * scale}px`,
            }}
          >
            <ChartLegendItem />
            <ChartLegendItem />
            <ChartLegendItem />
            <ChartLegendItem />
            <ChartLegendItem />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmptyState;
